/**
 * Pre-Task Safety Plan Component
 * ===============================
 *
 * Individual form component
 *
 * Base Components:
 * - EXTENDS BaseFormComponent<PfizerPreTaskSafetyPlan> for form logic inheritance
 * - USES FormLayoutComponent for step navigation UI (in template)
 *
 * Form Configuration:
 * - Form structure, steps, sections, and field organization are defined in the form model
 * - Signature and upload requirements are configured via form model metadata
 * - Component implements abstract methods: initializeForm() and getFormData()
 * - Component overrides setupDefaultStepValidation() for custom step validation rules
 *
 * Key Functionality:
 * - Creates nested form groups for logical organization (jobDetails, jobAnalysis, permitsHazards, hazardEvaluation, crewSignatures)
 * - Implements dynamic hazard control management (up to 7 hazard-control pairs)
 * - Provides comprehensive safety planning with job analysis and hazard identification
 * - Implements checkbox groups for permits, hazard conditions, and control methods
 * - Implements form validation for safety compliance and planning requirements
 * - Implements step validation with signature requirements for foreperson
 * - Flattens nested form structure for backend submission
 *
 * Unique Complex Logic:
 * - Dynamic hazard control field management with add/remove functionality (up to 7 pairs)
 * - Conditional validation for hazard control fields that become required only when corresponding hazard fields have values
 * - Dynamic crew member management with add/remove functionality (up to 3 additional members)
 * - Real-time validation updates for hazard control fields with subscription management
 * - Boolean array tracking for dynamic UI rendering of hazard controls and crew members
 *
 * Form Model vs Component Responsibilities:
 * - FORM MODEL: Defines step/section structure, field mappings, attachment requirements, and form metadata
 * - COMPONENT: Handles Angular form validation, form group creation, conditional field logic, and overrides base form methods
 *
 * @author MacSafety Development Team
 * @version 1.0
 */

import { Component, OnInit, OnDestroy, signal, effect, computed } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { BaseFormComponent } from '../../base-form.component';
import { FormStep } from '../../../../../models/forms/form-steps.interface';
import { PfizerPreTaskSafetyPlan } from '../../../../../models/forms/pfizer-pre-task-safety-plan/pfizer-pre-task-safety-plan.model';
import { FormService } from '../../../../../services/form.service';
import { EntraService } from '../../../../../services/entra.service';
import { AttachmentUploadService } from '../../../../../services/attachment-upload.service';
import { SignatureService } from '../../../../../services/signature.service';
import { ToastService } from '../../../../../services/toast.service';
import { ScreenSizeService } from '../../../../../services/screen-size.service';
import { FormModelService } from '../../../../../services/form-model.service';
import { WeeklySiteInspection } from '../../../../../models/forms/weekly-site-inspection/weekly-site-inspection.model';
import { PfizerPreTaskTemplateListItem } from '../../../../../models/forms/pfizer-pre-task-safety-plan/pfizer-form-templates/pfizer-pre-task-template-list-item.model';
import { TemplateListResponse } from '../../../../../models/form-entry-lists/template-list-response.model';
import { PfizerPreTaskTemplate } from '../../../../../models/forms/pfizer-pre-task-safety-plan/pfizer-form-templates/pfizer-pre-task-template.model';
import { FormDocument } from '../../../../../models/forms/form-retrieval-response';
import { ActionSheetController, ModalController } from '@ionic/angular';
import { PfizerTemplateManagerModalComponent } from './pfizer-template-manager-modal/pfizer-template-manager-modal.component';
import { PfizerTemplateFormModalComponent } from './pfizer-template-form-modal/pfizer-template-form-modal.component';

@Component({
  selector: 'app-pfizer-pre-task-safety-plan',
  templateUrl: './pfizer-pre-task-safety-plan.component.html',
  styleUrls: ['./pfizer-pre-task-safety-plan.component.css'],
})
export class PfizerPreTaskSafetyPlanComponent
  extends BaseFormComponent<PfizerPreTaskSafetyPlan>
  implements OnInit, OnDestroy
{
  override formType = PfizerPreTaskSafetyPlan.getFormInfo().formType;
  override model = new PfizerPreTaskSafetyPlan();

  // UI state
  loadingState = signal<{ isLoading: boolean; message: string }>({
    isLoading: false,
    message: '',
  });
  openTemplateConfigModal = signal<boolean>(false);

  // Data state
  formTemplateList = signal<PfizerPreTaskTemplateListItem[]>([]);
  selectedTemplateId = signal<string | null>(null);
  currentTemplate = signal<PfizerPreTaskTemplate | null>(null);
  templateInteractionMode = signal<'populate' | 'edit' | 'create' | null>(null);
  canEditTemplates = computed(() => this.entraService.user().canEditTemplates);


selectedTemplateTitle = computed(() => {
  const selectedId = this.selectedTemplateId();
  if (!selectedId) {
    return null;
  }
  const template = this.formTemplateList().find(t => t.id === selectedId);
  return template?.templateTitle ?? null;
});

  private hazardSubscriptions: Subscription[] = [];

  requiredPermitsCheckboxes: { name: string; label: string }[] = [];
  hazardConditionsCheckboxes: { name: string; label: string }[] = [];
  controlMethodsCheckboxes: { name: string; label: string }[] = [];

  crewMemberCount: number = 0;
  showAddCrewMemberButton: boolean = true;
  showCrewMemberIndex: Array<boolean> = [false, false, false];

  hazardControlCount: number = 1;
  showAddHazardControlButton: boolean = true;
  showHazardControlIndex: Array<boolean> = [
    true,
    false,
    false,
    false,
    false,
    false,
    false,
  ];

  // Define the form steps and their properties
  override steps: FormStep[] = PfizerPreTaskSafetyPlan.getFormSteps();

  constructor(
    protected override fb: FormBuilder,
    protected override formService: FormService,
    protected override router: Router,
    protected override entraService: EntraService,
    protected override attachmentService: AttachmentUploadService,
    protected override toastService: ToastService,
    protected override signatureService: SignatureService,
    protected override screenSizeService: ScreenSizeService,
    protected formModelService: FormModelService,
    private modalController: ModalController,
    private actionSheetController: ActionSheetController
  ) {
    super(
      fb,
      formService,
      router,
      entraService,
      attachmentService,
      toastService,
      signatureService,
      screenSizeService
    );

    this.formMetadata = this.formModelService.getFormMetadata(this.formType)!;
    this.formService.fetchTemplatesByType<PfizerPreTaskTemplateListItem>(
      'pfizer-pre-task-template'
    );
    this.setupEffects();
  }

  override ngOnInit(): void {
    super.ngOnInit();
  }

  // ========================================
  // REACTIVE EFFECTS AND SUBSCRIPTIONS
  // ========================================

  /**
   * Sets up reactive effects for automatic state synchronization.
   * Connects component state to service state changes.
   *
   * @private
   */
  private setupEffects(): void {
    // Loading state effect
    effect(
      () => {
        const loadingState = this.formService.dataLoading();
        this.loadingState.set(loadingState);
      },
      { allowSignalWrites: true }
    );

    // Form entries effect
    effect(
      () => {
        const result = this.formService.templateListResult();
        this.handleTemplateListResult(result);
      },
      { allowSignalWrites: true }
    );

    // Current template effect
     effect(() => {
    const templateResponse = this.formService.currentTemplate();
    const mode = this.templateInteractionMode();
    
    // Guard: only proceed if we have both template data AND a mode set
    if (!templateResponse || !mode) {
      return;
    }
    
    if (mode === 'populate') {
      // Existing behavior - populate the form
      this.handleTemplateData(templateResponse);
      // Reset mode after handling
      this.templateInteractionMode.set(null);
    } else if (mode === 'edit') {
      // New behavior - open template form modal for editing
      this.openTemplateFormModal('edit', templateResponse);
      // Don't reset mode here - let modal dismiss handle it
    }
  }, { allowSignalWrites: true });
}

  /**
   * Handles the template list result from the service.
   *
   * @param result - The template list response from the service
   * @private
   */
  private handleTemplateListResult(
    result: TemplateListResponse<any> | null
  ): void {
    if (!result) {
      this.formTemplateList.set([]);
      return;
    }
    const templates = result.templates.items.map((item) =>
      PfizerPreTaskTemplateListItem.fromApiResponse(item)
    );
    console.log('Loaded form templates:', templates);
    this.formTemplateList.set(templates);
  }

  /**
   * Handles populating the form with data from a selected template.
   * Patches all nested form groups (jobDetails, jobAnalysis, permitsHazards, hazardEvaluation, crewSignatures)
   * with the corresponding values from the template's formEntryData.
   * Marks the form as dirty after populating to indicate unsaved changes.
   *
   * @param template - The template document containing pre-configured form data
   * @private
   */
  private handleTemplateData(
    template: FormDocument<PfizerPreTaskTemplate> | null
  ): void {
    if (!template || !template.formEntryData) {
      return;
    }

    console.log('Loaded template data:', template);
    const data = template.formEntryData;

    // Patch the nested form structure
    this.form.patchValue({
      jobDetails: {
        subcontractor: data.subcontractor,
        jobName: data.jobName,
        jobNumber: data.jobNumber,
        taskDescription: data.taskDescription,
        date: data.date,
      },
      jobAnalysis: {
        jobSteps: data.jobSteps,
        identifiedHazard1: data.identifiedHazard1,
        identifiedHazard2: data.identifiedHazard2,
        identifiedHazard3: data.identifiedHazard3,
        identifiedHazard4: data.identifiedHazard4,
        identifiedHazard5: data.identifiedHazard5,
        identifiedHazard6: data.identifiedHazard6,
        identifiedHazard7: data.identifiedHazard7,
        hazardControl1: data.hazardControl1,
        hazardControl2: data.hazardControl2,
        hazardControl3: data.hazardControl3,
        hazardControl4: data.hazardControl4,
        hazardControl5: data.hazardControl5,
        hazardControl6: data.hazardControl6,
        hazardControl7: data.hazardControl7,
      },
      permitsHazards: {
        requiredPermits: {
          confinedSpacePermit: data.confinedSpacePermit,
          hotWorkPermit: data.hotWorkPermit,
          lockoutTagoutPermit: data.lockoutTagoutPermit,
          otherPermit: data.otherPermit,
        },
        otherPermitDescription: data.otherPermitDescription,
        fireExplosionToxicHazard: data.fireExplosionToxicHazard,
        sdsReviewRequired: data.sdsReviewRequired,
      },
      hazardEvaluation: {
        hazardConditions: {
          contactingTemperatureExtremes: data.contactingTemperatureExtremes,
          contactingElectricalCurrent: data.contactingElectricalCurrent,
          environmentalAirborneRelease: data.environmentalAirborneRelease,
          movingObjectsEquipment: data.movingObjectsEquipment,
          hazardousSubstance: data.hazardousSubstance,
          obstructionInterference: data.obstructionInterference,
          struckBy: data.struckBy,
          fallSlipTrip: data.fallSlipTrip,
          materialHandling: data.materialHandling,
          stuckAgainst: data.stuckAgainst,
          caughtInBetween: data.caughtInBetween,
          otherHazardConditions: data.otherHazardConditions,
        },
        otherConditions: data.otherConditions,
        controlMethods: {
          ventilationOfExposureArea: data.ventilationOfExposureArea,
          isolationOfHazardFromWorker: data.isolationOfHazardFromWorker,
          substitutionOfHazardWithLessSevereOne:
            data.substitutionOfHazardWithLessSevereOne,
          eliminationOfHazard: data.eliminationOfHazard,
          changeOfWorkMethods: data.changeOfWorkMethods,
          goodWorkPractices: data.goodWorkPractices,
          personalProtectiveEquipment: data.personalProtectiveEquipment,
          otherControlMethods: data.otherControlMethods,
        },
        otherControlMethod: data.otherControlMethod,
      },
      crewSignatures: {
        forepersonFirstName: data.forepersonFirstName,
        forepersonLastName: data.forepersonLastName,
        crewMember1FirstName: data.crewMember1FirstName,
        crewMember1LastName: data.crewMember1LastName,
        crewMember2FirstName: data.crewMember2FirstName,
        crewMember2LastName: data.crewMember2LastName,
        crewMember3FirstName: data.crewMember3FirstName,
        crewMember3LastName: data.crewMember3LastName,
        otherNotes: data.otherNotes,
      },
    });

    this.currentTemplate.set(template.formEntryData);

      // Show dynamic fields that have values in the template
  this.showPopulatedDynamicFields(data);

    // Mark as touched so validation displays and user knows form has been modified
    this.form.markAsTouched();
    this.form.markAsDirty();

    // Also mark all child controls as touched
    this.markFormGroupTouched(this.form);
  }



  /**
 * Automatically shows hazard control and crew member fields that have values in the template data.
 * This ensures that when a template is loaded, all populated fields are visible to the user.
 *
 * @param data - The template data to check for populated fields
 * @private
 */
private showPopulatedDynamicFields(data: PfizerPreTaskTemplate): void {
  // Check and show hazard control fields (2-7, since 1 is always visible)
  for (let i = 2; i <= 7; i++) {
    const hazardValue = data[`identifiedHazard${i}` as keyof PfizerPreTaskTemplate];
    const controlValue = data[`hazardControl${i}` as keyof PfizerPreTaskTemplate];
    
    if ((hazardValue && hazardValue !== '') || (controlValue && controlValue !== '')) {
      this.showHazardControlIndex[i - 1] = true;
      this.hazardControlCount = Math.max(this.hazardControlCount, i);
    }
  }
  
  // Update the add button visibility
  this.showAddHazardControlButton = this.hazardControlCount < 7;

  // Check and show crew member fields (1-3)
  for (let i = 1; i <= 3; i++) {
    const firstNameValue = data[`crewMember${i}FirstName` as keyof PfizerPreTaskTemplate];
    const lastNameValue = data[`crewMember${i}LastName` as keyof PfizerPreTaskTemplate];
    
    if ((firstNameValue && firstNameValue !== '') || (lastNameValue && lastNameValue !== '')) {
      this.showCrewMemberIndex[i - 1] = true;
      this.crewMemberCount = Math.max(this.crewMemberCount, i);
    }
  }
  
  // Update the add button visibility
  this.showAddCrewMemberButton = this.crewMemberCount < 3;
}


  // Helper method to recursively mark all controls
  private markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();
      control?.markAsDirty();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  /**
   * Handles template selection by the user:
   * Sets the interaction mode to 'populate'
   * Sets the selected template ID signal
   * Fetches the full template data from the service
   *
   * @param templateId
   */
  onTemplateSelected(templateId: string): void {
    this.templateInteractionMode.set('populate');
    this.selectedTemplateId.set(templateId);
    console.log('Selected template ID:', templateId ? templateId : 'None');
    this.formService.fetchFormTemplate<PfizerPreTaskTemplate>(templateId);
  }




  async openTemplateActionSheet() {
  const buttons = [];
  
  // Add template selection buttons
  this.formTemplateList().forEach(template => {
    buttons.push({
      text: template.templateTitle,
      cssClass: 'template-select-button',
      handler: () => {
        this.onTemplateSelected(template.id);
      }
    });
  });
  
  // Add create new button
  buttons.push({
    text: 'Create New Template',
    cssClass: 'template-create-button',
    handler: () => {
      this.onCreateNewTemplate();
    }
  });
  
  // Present action sheet
  const actionSheet = await this.actionSheetController.create({
    header: 'Select or Manage Templates',
    buttons: buttons
  });
  
  await actionSheet.present();
}

  /**
   * Initializes the form with its controls and validators.
   * Sets up the form group with all required fields and their validation rules.
   */
  protected override initializeForm(): void {
    // Initialize checkbox groupings
    this.requiredPermitsCheckboxes = this.getRequiredPermitsCheckboxes();
    this.hazardConditionsCheckboxes = this.getHazardConditionsCheckboxes();
    this.controlMethodsCheckboxes = this.getControlMethodsCheckboxes();

    // Create checkbox form groups
    const requiredPermitsGroup = this.fb.group({});
    this.requiredPermitsCheckboxes.forEach((checkbox) => {
      requiredPermitsGroup.addControl(
        checkbox.name,
        this.fb.control(
          this.model[checkbox.name as keyof PfizerPreTaskSafetyPlan] || false
        )
      );
    });

    const hazardConditionsGroup = this.fb.group({});
    this.hazardConditionsCheckboxes.forEach((checkbox) => {
      hazardConditionsGroup.addControl(
        checkbox.name,
        this.fb.control(
          this.model[checkbox.name as keyof PfizerPreTaskSafetyPlan] || false
        )
      );
    });

    const controlMethodsGroup = this.fb.group({});
    this.controlMethodsCheckboxes.forEach((checkbox) => {
      controlMethodsGroup.addControl(
        checkbox.name,
        this.fb.control(
          this.model[checkbox.name as keyof PfizerPreTaskSafetyPlan] || false
        )
      );
    });

    this.form = this.fb.group({
      jobDetails: this.fb.group({
        subcontractor: ['MacDonald-Miller Facility Solutions'],
        jobName: [this.model.jobName],
        jobNumber: [this.model.jobNumber],
        taskDescription: [this.model.taskDescription, Validators.required],
        date: [this.model.date, Validators.required],
      }),
      jobAnalysis: this.fb.group({
        jobSteps: [this.model.jobSteps, Validators.required],

        identifiedHazard1: [this.model.identifiedHazard1, Validators.required],
        identifiedHazard2: [this.model.identifiedHazard2],
        identifiedHazard3: [this.model.identifiedHazard3],
        identifiedHazard4: [this.model.identifiedHazard4],
        identifiedHazard5: [this.model.identifiedHazard5],
        identifiedHazard6: [this.model.identifiedHazard6],
        identifiedHazard7: [this.model.identifiedHazard7],

        hazardControl1: [this.model.hazardControl1, Validators.required],
        hazardControl2: [this.model.hazardControl2],
        hazardControl3: [this.model.hazardControl3],
        hazardControl4: [this.model.hazardControl4],
        hazardControl5: [this.model.hazardControl5],
        hazardControl6: [this.model.hazardControl6],
        hazardControl7: [this.model.hazardControl7],
      }),
      permitsHazards: this.fb.group({
        requiredPermits: requiredPermitsGroup,
        otherPermitDescription: [this.model.otherPermitDescription],
        fireExplosionToxicHazard: [this.model.fireExplosionToxicHazard],
        sdsReviewRequired: [this.model.sdsReviewRequired],
      }),
      hazardEvaluation: this.fb.group({
        hazardConditions: hazardConditionsGroup,
        otherConditions: [this.model.otherConditions],
        controlMethods: controlMethodsGroup,
        otherControlMethod: [this.model.otherControlMethod],
      }),
      crewSignatures: this.fb.group({
        forepersonFirstName: [
          this.model.forepersonFirstName,
          Validators.required,
        ],
        forepersonLastName: [
          this.model.forepersonLastName,
          Validators.required,
        ],
        crewMember1FirstName: [this.model.crewMember1FirstName],
        crewMember1LastName: [this.model.crewMember1LastName],
        crewMember2FirstName: [this.model.crewMember2FirstName],
        crewMember2LastName: [this.model.crewMember2LastName],
        crewMember3FirstName: [this.model.crewMember3FirstName],
        crewMember3LastName: [this.model.crewMember3LastName],
        otherNotes: [this.model.otherNotes],
      }),
    });

    // Set up dynamic validation for hazard control fields
    this.setupHazardControlValidation();
  }

  // Static methods to get the checkbox groupings for the component
  getRequiredPermitsCheckboxes(): { name: string; label: string }[] {
    return [
      {
        name: 'confinedSpacePermit',
        label: this.fieldMappings['confinedSpacePermit'],
      },
      { name: 'hotWorkPermit', label: this.fieldMappings['hotWorkPermit'] },
      {
        name: 'lockoutTagoutPermit',
        label: this.fieldMappings['lockoutTagoutPermit'],
      },
      { name: 'otherPermit', label: this.fieldMappings['otherPermit'] },
    ];
  }

  getHazardConditionsCheckboxes(): { name: string; label: string }[] {
    return [
      {
        name: 'contactingTemperatureExtremes',
        label: this.fieldMappings['contactingTemperatureExtremes'],
      },
      {
        name: 'contactingElectricalCurrent',
        label: this.fieldMappings['contactingElectricalCurrent'],
      },
      {
        name: 'environmentalAirborneRelease',
        label: this.fieldMappings['environmentalAirborneRelease'],
      },
      {
        name: 'movingObjectsEquipment',
        label: this.fieldMappings['movingObjectsEquipment'],
      },
      {
        name: 'hazardousSubstance',
        label: this.fieldMappings['hazardousSubstance'],
      },
      {
        name: 'obstructionInterference',
        label: this.fieldMappings['obstructionInterference'],
      },
      { name: 'struckBy', label: this.fieldMappings['struckBy'] },
      { name: 'fallSlipTrip', label: this.fieldMappings['fallSlipTrip'] },
      {
        name: 'materialHandling',
        label: this.fieldMappings['materialHandling'],
      },
      { name: 'stuckAgainst', label: this.fieldMappings['stuckAgainst'] },
      { name: 'caughtInBetween', label: this.fieldMappings['caughtInBetween'] },
      {
        name: 'otherHazardConditions',
        label: this.fieldMappings['otherHazardConditions'],
      },
    ];
  }

  getControlMethodsCheckboxes(): { name: string; label: string }[] {
    return [
      {
        name: 'ventilationOfExposureArea',
        label: this.fieldMappings['ventilationOfExposureArea'],
      },
      {
        name: 'isolationOfHazardFromWorker',
        label: this.fieldMappings['isolationOfHazardFromWorker'],
      },
      {
        name: 'substitutionOfHazardWithLessSevereOne',
        label: this.fieldMappings['substitutionOfHazardWithLessSevereOne'],
      },
      {
        name: 'eliminationOfHazard',
        label: this.fieldMappings['eliminationOfHazard'],
      },
      {
        name: 'changeOfWorkMethods',
        label: this.fieldMappings['changeOfWorkMethods'],
      },
      {
        name: 'goodWorkPractices',
        label: this.fieldMappings['goodWorkPractices'],
      },
      {
        name: 'personalProtectiveEquipment',
        label: this.fieldMappings['personalProtectiveEquipment'],
      },
      {
        name: 'otherControlMethods',
        label: this.fieldMappings['otherControlMethods'],
      },
    ];
  }

  // Form group getters
  get jobDetailsFormGroup(): FormGroup {
    return this.form.get('jobDetails') as FormGroup;
  }

  get jobAnalysisFormGroup(): FormGroup {
    return this.form.get('jobAnalysis') as FormGroup;
  }

  get permitsHazardsFormGroup(): FormGroup {
    return this.form.get('permitsHazards') as FormGroup;
  }

  get requiredPermitsFormGroup(): FormGroup {
    return this.permitsHazardsFormGroup.get('requiredPermits') as FormGroup;
  }

  get hazardEvaluationFormGroup(): FormGroup {
    return this.form.get('hazardEvaluation') as FormGroup;
  }

  get hazardConditionsFormGroup(): FormGroup {
    return this.hazardEvaluationFormGroup.get('hazardConditions') as FormGroup;
  }

  get controlMethodsFormGroup(): FormGroup {
    return this.hazardEvaluationFormGroup.get('controlMethods') as FormGroup;
  }

  get crewSignaturesFormGroup(): FormGroup {
    return this.form.get('crewSignatures') as FormGroup;
  }

  /**
   * Retrieves the current form data and creates a new PreTaskSafetyPlan instance.
   * This method is called when submitting the form.
   */
  protected override getFormData(): PfizerPreTaskSafetyPlan {
    const formValue = this.form.value;

    // Flatten the nested form groups into a single object
    const flattenedData = {
      // Job details
      ...formValue.jobDetails,

      // Job analysis - individual fields (no longer arrays)
      ...formValue.jobAnalysis,

      // Permits & hazards (flatten checkbox groups)
      ...formValue.permitsHazards.requiredPermits,
      otherPermitDescription: formValue.permitsHazards.otherPermitDescription,
      fireExplosionToxicHazard:
        formValue.permitsHazards.fireExplosionToxicHazard,
      sdsReviewRequired: formValue.permitsHazards.sdsReviewRequired,

      // Hazard evaluation (flatten checkbox groups)
      ...formValue.hazardEvaluation.hazardConditions,
      otherConditions: formValue.hazardEvaluation.otherConditions,
      ...formValue.hazardEvaluation.controlMethods,
      otherControlMethod: formValue.hazardEvaluation.otherControlMethod,

      // Crew signatures
      ...formValue.crewSignatures,
    };

    const preTaskSafetyPlan = new PfizerPreTaskSafetyPlan(flattenedData);
    const updatedPreTaskSafetyPlan =
      this.updateModelWithAttachments(preTaskSafetyPlan);
    return updatedPreTaskSafetyPlan;
  }

  /**
   * Sets up validation rules for each step of the pre-task safety plan form.
   * Each step has specific validation requirements that must be met before proceeding:
   * - Job Details: Requires basic job information
   * - Job Analysis: Requires job steps, hazards, and controls
   * - Permits & Hazards: Requires hazard assessment questions
   * - Hazard Evaluation: Optional conditions and methods
   * - Crew Signatures: Requires crew member information and signatures
   *
   * The validation ensures that:
   * 1. All required fields are filled out
   * 2. Required signatures are collected
   * 3. Form data meets safety and compliance requirements
   */
  protected override setupDefaultStepValidation(): void {
    // Skip validation setup if noFormRequirements is true
    if (!this.shouldApplyFormRequirements()) {
      return;
    }

    const forepersonSignatureIndex: number = 0;

    // Custom validation rules for each step using the form groups directly
    this.setupStepValidation('job-details', this.jobDetailsFormGroup);
    this.setupStepValidation('job-analysis', this.jobAnalysisFormGroup);
    this.setupStepValidation('permits-hazards', this.permitsHazardsFormGroup);
    this.setupStepValidation(
      'hazard-evaluation',
      this.hazardEvaluationFormGroup
    );
    this.setupStepValidation('crew-signatures', this.crewSignaturesFormGroup, {
      signatureIndex: [forepersonSignatureIndex],
    });
  }

  ngOnDestroy(): void {
    this.hazardSubscriptions.forEach((subscription) =>
      subscription.unsubscribe()
    );
  }

  private setupHazardControlValidation(): void {
    const jobAnalysisGroup = this.jobAnalysisFormGroup;

    // Set up subscriptions for hazards 2-13 (hazard 1 is always required)
    for (let i = 2; i <= 7; i++) {
      const hazardField = `identifiedHazard${i}`;
      const controlField = `hazardControl${i}`;

      const subscription = jobAnalysisGroup
        .get(hazardField)!
        .valueChanges.subscribe((value) => {
          const controlControl = jobAnalysisGroup.get(controlField)!;

          if (value && value.trim() !== '') {
            // Add required validator if hazard has a value
            controlControl.setValidators([Validators.required]);
          } else {
            // Remove required validator if hazard is empty
            controlControl.clearValidators();
          }

          controlControl.updateValueAndValidity();
        });

      this.hazardSubscriptions.push(subscription);
    }
  }

  addCrewMember() {
    if (this.crewMemberCount < 3) {
      this.crewMemberCount++;
      const crewMemberCount = this.crewMemberCount++;

      this.showCrewMemberIndex[crewMemberCount - 1] = true;

      this.crewMemberCount = crewMemberCount;
      if (crewMemberCount === 3) {
        this.showAddCrewMemberButton = false;
      }
    }
  }

  addHazardControl() {
    if (this.hazardControlCount < 7) {
      this.hazardControlCount++;
      const hazardControlCount = this.hazardControlCount++;
      this.showHazardControlIndex[hazardControlCount - 1] = true;
      this.hazardControlCount = hazardControlCount;
      if (hazardControlCount === 7) {
        this.showAddHazardControlButton = false;
      }
    }
  }

/**
 * Opens the template form modal in either create or edit mode
 * @param mode - 'create' or 'edit'
 * @param templateData - The template data (only provided in edit mode)
 */
async openTemplateFormModal(
  mode: 'create' | 'edit', 
  templateData?: FormDocument<PfizerPreTaskTemplate> | null
): Promise<void> {
  const modal = await this.modalController.create({
    component: PfizerTemplateFormModalComponent,
    componentProps: {
      mode: mode,
      templateData: templateData,
      existingTemplateTitles: this.formTemplateList().map(t => t.templateTitle),
      fieldMappings: this.fieldMappings,
      requiredFields: this.requiredFields
    }
  });

  await modal.present();

  const { data } = await modal.onWillDismiss();

  // If template was successfully created/updated, refresh the template list
  if (data?.success) {
    this.formService.fetchTemplatesByType<PfizerPreTaskTemplateListItem>('pfizer-pre-task-template');
    
    // Reset the interaction mode
    this.templateInteractionMode.set(null);
    
    this.toastService.showSuccessMessage(
      mode === 'create' ? 'Template created successfully' : 'Template updated successfully'
    );
  }
}


  /**
 * Opens the template manager bottom sheet
 */
async openTemplateManager() {
  const modal = await this.modalController.create({
    component: PfizerTemplateManagerModalComponent,
    componentProps: {
      templates: this.formTemplateList(),
      canEditTemplates: this.canEditTemplates() 
    },
    breakpoints: [0, 0.5, 0.75, 1],
    initialBreakpoint: 1,
    handle: true
  });

  await modal.present();

  const { data } = await modal.onWillDismiss();

  if (data) {
    switch (data.action) {
      case 'select':
        this.onTemplateSelected(data.templateId);
        break;
      case 'edit':
        this.onEditTemplate(data.templateId);
        break;
      case 'delete':
        this.onDeleteTemplate(data.templateId);
        break;
      case 'create':
        this.onCreateNewTemplate();
        break;
    }
  }
}

/**
 * Handles editing a template - fetches data and opens form modal
 */
onEditTemplate(templateId: string): void {
  this.templateInteractionMode.set('edit');
  this.selectedTemplateId.set(templateId);
  this.formService.fetchFormTemplate<PfizerPreTaskTemplate>(templateId);
}

/**
 * Handles deleting a template
 */
onDeleteTemplate(templateId: string): void {
      this.formService.submitTemplateDelete(templateId, 'pfizer-pre-task-template');
}

/**
 * Handles creating a new template
 */
onCreateNewTemplate(): void {
  this.templateInteractionMode.set('create');
  this.openTemplateFormModal('create', null);
}




}
